import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, HISTORY_STATE, adminApi } from '@wa/shared';
import { Cell, DataTable, Empty, Page } from '@/components/Primitives';
import { ConnectCoexistence } from '@/components/ConnectCoexistence';

/**
 * The numbers, and the two things that could previously only be done in SQL.
 *
 * <b>Re-check</b> asks Meta what a number's quality rating and messaging tier are
 * now. Neither is pushed to us, so a number whose tier Meta has raised keeps being
 * throttled to the smallest one until somebody asks — which nothing could, until now.
 *
 * <b>Connect a phone</b> is the coexistence path: a number the client keeps
 * answering in the WhatsApp Business app, shared with the Cloud API rather than
 * migrated away from it. It is here rather than in the client's own portal because it
 * spends a Meta app secret PSM holds, and because the operator is on the phone with
 * the client while it runs.
 *
 * <b>Disable</b> takes a number out of service. Dispatch reads the same flag the
 * send-from list does, so it is the whole of "stop using this number". It is not a
 * delete: the channel owns every message ever sent through it.
 */
export function ChannelsPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  const channels = useQuery({ queryKey: ['admin', 'channels'], queryFn: adminApi.channels });

  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? e.message : fallback);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'channels'] });

  const recheck = useMutation({
    mutationFn: (id: number) => adminApi.recheckChannel(id),
    onSuccess: (result) => {
      setError(null);
      refresh();
      setNote(result.ok
        ? `Meta says: ${result.verifiedName ?? 'unnamed'}, quality ${result.qualityRating ?? 'unknown'}, tier ${result.messagingLimitTier ?? 'unknown'}.`
        : `Meta refused: ${result.problem ?? 'no reason given'}.`);
    },
    onError: (e) => fail(e, 'Could not reach Meta for that number.'),
  });

  const disable = useMutation({
    mutationFn: (id: number) => adminApi.disableChannel(id),
    onSuccess: () => { setError(null); setNote(null); refresh(); },
    onError: (e) => fail(e, 'Could not disable that number.'),
  });

  return (
    <Page
      wide
      title="Channels"
      subtitle="WhatsApp numbers and the Meta App each one belongs to. Access tokens are never sent to this portal."
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setConnecting(true)}
          className="rounded border border-(--color-line) px-3 py-1.5 text-small font-medium hover:text-(--color-ink)"
        >
          Connect a phone the client still uses
        </button>
        <span className="text-micro text-(--color-muted)">
          Coexistence — the WhatsApp Business app keeps working on the handset.
        </span>
      </div>

      {connecting && (
        <ConnectCoexistence
          onClose={() => setConnecting(false)}
          onConnected={(summary) => { setConnecting(false); setError(null); setNote(summary); refresh(); }}
        />
      )}

      {error && (
        <p role="alert" className="mb-3 rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {note && (
        <p className="mb-3 rounded border border-(--color-line) px-3 py-2 text-small text-(--color-muted)">
          {note}
        </p>
      )}

      <DataTable head={['Number', 'Tenant', 'Meta App', 'Webhook slug', 'Quality', 'Tier', 'Rate/s', 'Active', '']}>
        {channels.data?.map((c) => (
          <tr key={c.id}>
            <Cell nowrap>
              <span className="font-medium">{c.displayPhoneNumber ?? '—'}</span>
              <span className="block font-mono text-small text-(--color-muted)">{c.phoneNumberId}</span>
              {/*
                Under the number rather than in a column of its own. It is a property
                of this number — capped throughput, replies that arrive from the
                handset, a bot that steps aside — and a tenth column pushed the action
                buttons out of the table's scroller, where nothing said they were.
              */}
              {c.isCoexistence && (
                <span className="mt-0.5 block text-micro">
                  <span className="rounded-full border border-(--color-line) px-1.5 py-0.5 text-(--color-ink-soft)">
                    phone + API
                  </span>
                  {c.historyState > 0 && (
                    <span
                      className={`ml-1 ${c.historyState === 4 ? 'text-(--color-bad)' : 'text-(--color-muted)'}`}
                      title={c.historyError ?? undefined}
                    >
                      history {HISTORY_STATE[c.historyState] ?? c.historyState}
                      {c.historyMessages > 0 ? ` · ${c.historyMessages.toLocaleString('en-IN')}` : ''}
                    </span>
                  )}
                </span>
              )}
            </Cell>
            <Cell>{c.tenantName}</Cell>
            <Cell>{c.metaAppName}</Cell>
            <Cell mono nowrap>/webhooks/whatsapp/{c.webhookSlug}</Cell>
            <Cell nowrap>{c.qualityRating ?? '—'}</Cell>
            <Cell nowrap>
              {c.messagingLimitTier ?? (
                <span className="text-(--color-warn)" title="Unknown, so the dispatcher assumes the smallest tier">
                  unknown
                </span>
              )}
            </Cell>
            <Cell>{c.sendRatePerSecond}</Cell>
            <Cell>
              <span className={c.isActive ? 'text-(--color-good)' : 'text-(--color-bad)'}>
                {c.isActive ? 'yes' : 'no'}
              </span>
            </Cell>
            <Cell>
              {/*
                Not flex-wrap. Wrapping let the table size this column to one button
                and stack them, doubling the height of every row on a screen with
                room for both; the table already scrolls sideways when there is not.
              */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => recheck.mutate(c.id)}
                  disabled={recheck.isPending}
                  className="rounded border border-(--color-line) px-2 py-1 text-small whitespace-nowrap hover:text-(--color-ink) disabled:opacity-50"
                >
                  Re-check
                </button>
                {/* Absent rather than refused: there is nothing to disable twice. */}
                {c.isActive && (
                  <button
                    type="button"
                    onClick={() => disable.mutate(c.id)}
                    disabled={disable.isPending}
                    className="rounded border border-(--color-line) px-2 py-1 text-small whitespace-nowrap hover:text-(--color-bad) disabled:opacity-50"
                  >
                    Disable
                  </button>
                )}
              </div>
            </Cell>
          </tr>
        ))}
      </DataTable>
      {channels.data?.length === 0 && <Empty>No channels registered.</Empty>}
    </Page>
  );
}
